import React, { useEffect, useRef, useState } from "react";

import HttpManager from "../../../services/HttpManager";

/**
 * @menciones de WappTeam con teclado (Nancy, 2026-10-15: "para seleccionarla tengo que hacerlo con el cursor y no con el
 * teclado"): ↑ ↓ para moverse, Enter o Tab para elegir, Esc para cerrar. Lo usan el cuadro de escribir, el comentario de las
 * fotos y la ventanita. Busca lo que va justo antes del cursor, así se puede mencionar a media frase.
 */

export interface Persona { id: number; nombre: string; }

const ARROBA = /(^|\s)@([A-Za-zÁÉÍÓÚÑáéíóúñü]{0,20})$/;
export const primerNombre = (p: Persona) => p.nombre.split(" ")[0];

export const useMenciones = (campusId: number, canalId: number | null | undefined) => {
	const [sugeridas, setSugeridas] = useState<Persona[]>([]);
	const [activa, setActiva] = useState(0);
	const pedido = useRef(0);   // la respuesta de una búsqueda vieja no pisa a la nueva
	// Espera 250 ms a que deje de teclear antes de preguntar (no una consulta al servidor por letra).
	const espera = useRef<ReturnType<typeof setTimeout> | null>(null);
	useEffect(() => () => { if (espera.current) clearTimeout(espera.current); }, []);

	const cerrar = () => { pedido.current++; if (espera.current) clearTimeout(espera.current); setSugeridas([]); setActiva(0); };

	/** En cada cambio del texto: si antes del cursor hay "@algo", busca a quién mencionar en esta conversación. */
	const revisar = (v: string, cursor?: number | null) => {
		const m = canalId ? ARROBA.exec(v.slice(0, cursor ?? v.length)) : null;
		if (!m) { if (sugeridas.length) cerrar(); else { pedido.current++; if (espera.current) clearTimeout(espera.current); } return; }
		const n = ++pedido.current;
		if (espera.current) clearTimeout(espera.current);
		espera.current = setTimeout(() => {
			HttpManager.getInstance().prospectsService.equipoPersonas(campusId, m[2], canalId)
				.then((r) => { if (n === pedido.current) { setSugeridas(r.data.personas.slice(0, 6)); setActiva(0); } })
				.catch(() => { if (n === pedido.current) setSugeridas([]); });
		}, 250);
	};

	/** Cambia el "@algo" de antes del cursor por "@Nombre " y deja el cursor después. Devuelve el texto nuevo. */
	const poner = (el: HTMLTextAreaElement | null, v: string, p: Persona, setV: (s: string) => void): string => {
		const pos = el && el === document.activeElement ? el.selectionStart : v.length;
		const antes = v.slice(0, pos).replace(ARROBA, "$1@" + primerNombre(p) + " ");
		const despues = v.slice(pos).replace(/^ /, "");
		const nuevo = antes + despues;
		setV(nuevo);
		cerrar();
		if (el) requestAnimationFrame(() => { el.focus(); el.setSelectionRange(antes.length, antes.length); });
		return nuevo;
	};

	/** Teclas con la lista abierta. Devuelve true si la tecla era para la lista (el cuadro ya no debe hacer nada con ella). */
	const tecla = (e: React.KeyboardEvent<HTMLTextAreaElement>, elegir: (p: Persona) => void): boolean => {
		if (!sugeridas.length || (e.nativeEvent as any).isComposing) return false;
		const n = sugeridas.length;
		if (e.key === "ArrowDown" || e.key === "ArrowUp") {
			e.preventDefault();
			setActiva((a) => (a + (e.key === "ArrowDown" ? 1 : n - 1)) % n);
			return true;
		}
		if (e.key === "Enter" || e.key === "Tab") {
			e.preventDefault();
			elegir(sugeridas[Math.min(activa, n - 1)]);
			return true;
		}
		if (e.key === "Escape") {
			e.preventDefault();
			e.stopPropagation();
			cerrar();
			return true;
		}
		return false;
	};

	return { sugeridas, activa, setActiva, revisar, poner, tecla, cerrar };
};

/** La lista de personas; el clic no le quita el foco al cuadro de escribir. */
export const ListaMenciones: React.FC<{ sugeridas: Persona[]; activa: number; onActiva: (i: number) => void; onElegir: (p: Persona) => void }> = ({ sugeridas, activa, onActiva, onElegir }) =>
	sugeridas.length ? (
		<div className="eq-sugeridas" role="listbox" aria-label="Mencionar a">
			{sugeridas.map((p, i) => (
				<button key={p.id} type="button" role="option" aria-selected={i === activa} className={i === activa ? "activa" : undefined}
					onMouseDown={(e) => e.preventDefault()} onMouseEnter={() => onActiva(i)} onClick={() => onElegir(p)}>
					<b>@{primerNombre(p)}</b> {p.nombre}
				</button>
			))}
		</div>
	) : null;

/** Ids de las personas elegidas cuyo "@Nombre" sigue en el texto (si lo borró, ya no se le avisa). */
export const idsMencionados = (elegidas: { id: number; nombre: string }[], texto: string) =>
	elegidas.filter((x) => texto.includes("@" + x.nombre)).map((x) => x.id);
